import { useEffect, useState } from "react";
import { CalendarCheck, Clock, CheckCheck, XCircle, DollarSign, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";

const STAT_CARDS = [
  { key: "total_bookings", label: "Total Bookings", icon: CalendarCheck, color: "#ff6b00" },
  { key: "pending", label: "Pending", icon: Clock, color: "#f59e0b" },
  { key: "awaiting_deposit", label: "Awaiting Deposit", icon: DollarSign, color: "#fb923c" },
  { key: "confirmed", label: "Confirmed", icon: CheckCheck, color: "#10b981" },
  { key: "completed", label: "Completed", icon: CheckCheck, color: "#10b981" },
  { key: "cancelled", label: "Cancelled", icon: XCircle, color: "#ef4444" },
  { key: "waitlisted", label: "Waitlisted", icon: Clock, color: "#a855f7" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/bookings/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setRecent(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Overview</div>
      <h1 className="font-display mt-2 text-3xl sm:text-4xl font-extrabold uppercase text-slate-100">
        Command Center
      </h1>

      <div className="mt-8 grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {STAT_CARDS.map((c) => (
          <div key={c.key} className="cj-card p-5" data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between">
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center"
                style={{ background: `${c.color}18` }}
              >
                <c.icon className="h-5 w-5" style={{ color: c.color }} />
              </div>
            </div>
            <div className="mt-3 font-display font-extrabold text-3xl text-slate-100">
              {stats ? stats[c.key] : "—"}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">
              {c.label}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="cj-card p-6" data-testid="stat-deposits-collected">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-[#ff6b00]" />
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
              Deposits Collected
            </div>
          </div>
          <div className="font-display font-extrabold text-4xl text-[#ff6b00] mt-3">
            ${stats ? stats.deposits_collected.toFixed(2) : "0.00"}
          </div>
        </div>
        <div className="cj-card p-6" data-testid="stat-pipeline-revenue">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#00f0ff]" />
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
              Pipeline Revenue (paid bookings)
            </div>
          </div>
          <div className="font-display font-extrabold text-4xl text-[#00f0ff] mt-3">
            ${stats ? stats.pipeline_revenue.toFixed(2) : "0.00"}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Recent bookings</div>
        <h2 className="font-display mt-2 text-xl sm:text-2xl font-extrabold uppercase text-slate-100">
          Latest activity
        </h2>
        <div className="mt-4 grid gap-3">
          {recent.length === 0 && (
            <div className="cj-card p-6 text-slate-400 text-sm">No bookings yet.</div>
          )}
          {recent.map((b) => (
            <div
              key={b.id}
              className="cj-card p-4 flex items-center justify-between gap-3"
              data-testid={`recent-booking-${b.id}`}
            >
              <div className="min-w-0">
                <div className="font-display uppercase font-bold text-slate-100 truncate">
                  {b.customer.name} · {b.vehicle.year} {b.vehicle.make} {b.vehicle.model}
                </div>
                <div className="font-mono text-xs text-slate-500 mt-1">
                  {b.scheduled_date} · {b.scheduled_time} · {b.service_name}
                </div>
              </div>
              <span className={`cj-pill border ${statusClass(b.status)}`}>{b.status.replace("_", " ")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function statusClass(s) {
  switch (s) {
    case "pending":
      return "bg-amber-500/10 text-amber-400 border-amber-500/30";
    case "awaiting_deposit":
      return "bg-orange-500/10 text-orange-300 border-orange-500/30";
    case "deposit_paid":
      return "bg-cyan-500/10 text-cyan-300 border-cyan-500/30";
    case "confirmed":
      return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30";
    case "in_progress":
      return "bg-blue-500/10 text-blue-300 border-blue-500/30";
    case "completed":
      return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
    case "cancelled":
      return "bg-red-500/10 text-red-400 border-red-500/30";
    case "waitlisted":
      return "bg-purple-500/10 text-purple-300 border-purple-500/30";
    case "declined":
      return "bg-slate-500/10 text-slate-400 border-slate-500/30";
    case "expired":
      return "bg-zinc-500/10 text-zinc-400 border-zinc-500/30";
    default:
      return "bg-white/5 text-slate-300 border-white/10";
  }
}
